import {
  useAllMatchStatsWC26,
  useBracketFixtures,
  useDirectFkGoals,
  useKeeperGoals,
  useOlimpicoGoals,
  useWC26ScoringRules,
  useWC26UserTeams,
} from '../../queries';
import { BracketFixture, MatchStats, UserTeams } from '../../types';
import { goalKey } from '../../utils/scoringUtils';
import NameLink from '../NameLink/NameLink';
import Spinner from '../Spinner/Spinner';
import './SpecialEventsWC26.css';

interface SpecialEvent {
  key: string;
  type: 'hattrick' | 'directfk' | 'olimpico' | 'goals3' | 'keeper' | 'mostreds';
  label: string;
  detail: string;
  users: string[];
  points: number;
  teamLogo: string;
  match: string;
  kickoff: string;
  kickoffTs: number;
  note?: string;
}

const BADGE: Record<string, string> = {
  hattrick: '🎩',
  directfk: '🎯',
  olimpico: '🌀',
  goals3: '⚽',
  keeper: '🧤',
  mostreds: '🟥',
};

const TYPE_LABEL: Record<string, string> = {
  hattrick: 'Hattrick',
  directfk: 'Direct Free Kick',
  olimpico: 'Olimpico',
  goals3: 'Team 3+ Goals',
  keeper: 'Goalkeeper Goal',
  mostreds: 'Most Red Cards',
};

function findUser(teamId: string, userTeams: UserTeams[]): string {
  return userTeams.find((u) => u.team1Id === teamId || u.team2Id === teamId)?.name ?? '';
}

function buildEvents(
  matchStats: MatchStats[],
  userTeams: UserTeams[],
  directFkKeys: string[],
  olimpicoKeys: string[],
  keeperKeys: string[],
  rules: Record<string, number>,
  fixturesById: Record<number, BracketFixture>
): SpecialEvent[] {
  const events: SpecialEvent[] = [];

  matchStats.forEach((match) => {
    const homeId = match.statistics[0]?.id.toString();
    const awayId = match.statistics[1]?.id.toString();
    const homeTeam = match.statistics[0]?.name ?? homeId;
    const awayTeam = match.statistics[1]?.name ?? awayId;
    const homeLogo = match.statistics[0]?.logo ?? '';
    const awayLogo = match.statistics[1]?.logo ?? '';
    const matchLabel = `${homeTeam} vs ${awayTeam}`;
    const fixture = fixturesById[match.id];
    const kickoffTs = fixture ? new Date(fixture.date).getTime() : 0;
    const kickoff = fixture ? new Date(fixture.date).toLocaleString([], {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }) : '';

    // Hattricks
    const goalsByPlayer:
     Record<string, { count: number; teamId: string; teamName: string; photo: string }> = {};
    match.goalEvents.forEach((g) => {
      if (g.type === 'Own Goal' || g.type === 'Missed Penalty') return;
      if (!goalsByPlayer[g.playerName]) {
        goalsByPlayer[g.playerName] = {
          count: 0, teamId: g.team.toString(), teamName: g.teamName, photo: g.playerPhoto,
        };
      }
      goalsByPlayer[g.playerName].count += 1;
    });
    Object.entries(goalsByPlayer).forEach(([player, {
      count, teamId, teamName, photo,
    }]) => {
      if (count >= 3) {
        events.push({
          key: `hattrick-${match.id}-${player}`,
          type: 'hattrick',
          label: TYPE_LABEL.hattrick,
          detail: `${player} (${teamName}) — ${count} goals`,
          users: [findUser(teamId, userTeams)].filter(Boolean),
          points: rules.hattrick ?? 0,
          teamLogo: photo || (teamId === homeId ? homeLogo : awayLogo),
          match: matchLabel,
          kickoff,
          kickoffTs,
        });
      }
    });

    // Team 3+ goals
    if (match.score.home >= 3 && homeId) {
      events.push({
        key: `goals3-${match.id}-home`,
        type: 'goals3',
        label: TYPE_LABEL.goals3,
        detail: `${homeTeam} scored ${match.score.home}`,
        users: [findUser(homeId, userTeams)].filter(Boolean),
        points: rules['3+goals'] ?? 0,
        teamLogo: homeLogo,
        match: matchLabel,
        kickoff,
        kickoffTs,
      });
    }
    if (match.score.away >= 3 && awayId) {
      events.push({
        key: `goals3-${match.id}-away`,
        type: 'goals3',
        label: TYPE_LABEL.goals3,
        detail: `${awayTeam} scored ${match.score.away}`,
        users: [findUser(awayId, userTeams)].filter(Boolean),
        points: rules['3+goals'] ?? 0,
        teamLogo: awayLogo,
        match: matchLabel,
        kickoff,
        kickoffTs,
      });
    }

    // Manual goal events
    match.goalEvents.forEach((g) => {
      const key = goalKey(match.id, g);
      const timeStr = g.extra ? `${g.time}+${g.extra}'` : `${g.time}'`;
      const detail = `${g.playerName} (${g.teamName}) ${timeStr}`;
      const user = findUser(g.team.toString(), userTeams);

      if (directFkKeys.includes(key)) {
        events.push({
          key: `directfk-${key}`,
          type: 'directfk',
          label: TYPE_LABEL.directfk,
          detail,
          users: [user].filter(Boolean),
          points: rules.directFk ?? 0,
          teamLogo: g.playerPhoto || g.teamLogo,
          match: matchLabel,
          kickoff,
          kickoffTs,
        });
      }
      if (olimpicoKeys.includes(key)) {
        events.push({
          key: `olimpico-${key}`,
          type: 'olimpico',
          label: TYPE_LABEL.olimpico,
          detail,
          users: [user].filter(Boolean),
          points: rules.olimpico ?? 0,
          teamLogo: g.playerPhoto || g.teamLogo,
          match: matchLabel,
          kickoff,
          kickoffTs,
        });
      }
      if (keeperKeys.includes(key)) {
        events.push({
          key: `keeper-${key}`,
          type: 'keeper',
          label: TYPE_LABEL.keeper,
          detail,
          users: [user].filter(Boolean),
          points: rules.keeperGoal ?? 0,
          teamLogo: g.playerPhoto || g.teamLogo,
          match: matchLabel,
          kickoff,
          kickoffTs,
        });
      }
    });
  });

  // Tournament-wide most reds — one card per team, sorted by the match where they clinched it
  const redsByTeam: Record<string, {
    reds: number;
    name: string;
    logo: string;
    matchLabel: string;
    kickoff: string;
    kickoffTs: number;
  }> = {};
  matchStats.forEach((match) => {
    const fix = fixturesById[match.id];
    const ts = fix ? new Date(fix.date).getTime() : 0;
    const ko = fix ? new Date(fix.date).toLocaleString([], {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }) : '';
    const h = match.statistics[0];
    const a = match.statistics[1];
    const ml = `${h?.name ?? ''} vs ${a?.name ?? ''}`;
    [h, a].forEach((team) => {
      if (!team || !(team.redCards ?? 0)) return;
      const id = team.id.toString();
      if (!redsByTeam[id]) {
        redsByTeam[id] = {
          reds: 0, name: team.name, logo: team.logo ?? '', matchLabel: ml, kickoff: ko, kickoffTs: ts,
        };
      }
      redsByTeam[id].reds += team.redCards ?? 0;
      if (ts > redsByTeam[id].kickoffTs) {
        redsByTeam[id].kickoffTs = ts;
        redsByTeam[id].kickoff = ko;
        redsByTeam[id].matchLabel = ml;
      }
    });
  });
  const allEntries = Object.entries(redsByTeam).filter(([, v]) => v.reds >= 2);
  if (allEntries.length > 0) {
    const maxReds = Math.max(...allEntries.map(([, v]) => v.reds));
    const topEntries = allEntries.filter(([, v]) => v.reds === maxReds);
    const tied = topEntries.length > 1;
    topEntries.forEach(([id, v]) => {
      events.push({
        key: `mostreds-${id}`,
        type: 'mostreds',
        label: TYPE_LABEL.mostreds,
        detail: `${v.name} — ${v.reds} red cards`,
        users: [findUser(id, userTeams)].filter(Boolean),
        points: tied ? 0 : (rules.mostReds ?? 0) + maxReds * Math.abs(rules.redCard ?? 0),
        teamLogo: v.logo,
        match: v.matchLabel,
        kickoff: v.kickoff,
        kickoffTs: v.kickoffTs,
        note: tied ? 'Tied for most red cards — no bonus awarded' : undefined,
      });
    });
  }

  return events.sort((a, b) => a.kickoffTs - b.kickoffTs);
}

function SpecialEventsWC26() {
  const [matchStatsLoading, matchStats] = useAllMatchStatsWC26();
  const [rulesLoading, rules] = useWC26ScoringRules();
  const [userTeamsLoading, userTeams] = useWC26UserTeams();
  const [, fixtures] = useBracketFixtures();
  const [, directFkGoals] = useDirectFkGoals();
  const [, olimpicoGoals] = useOlimpicoGoals();
  const [, keeperGoals] = useKeeperGoals();

  const loading = matchStatsLoading || rulesLoading || userTeamsLoading;

  const fixturesById = (fixtures ?? []).reduce<Record<number, BracketFixture>>((acc, f) => {
    acc[f.id] = f;
    return acc;
  }, {});

  const events = (!loading && matchStats && rules && userTeams)
    ? buildEvents(
      matchStats,
      userTeams,
      directFkGoals ?? [],
      olimpicoGoals ?? [],
      keeperGoals ?? [],
      rules as unknown as Record<string, number>,
      fixturesById
    )
    : [];

  return (
    <div className="special-events-wc26">
      <h2 className="special-events-wc26__title">Special Events</h2>
      {loading ? <Spinner /> : (
        events.length === 0
          ? <p className="special-events-wc26__empty">No special events yet</p>
          : events.map((e) => (
            <div key={e.key} className={`special-event${e.note ? ' special-event--tied' : ''}`}>
              <div className={`special-event-badge badge-${e.type}`}>
                {BADGE[e.type]}
              </div>
              {e.teamLogo && <img className="special-event-flag" src={e.teamLogo} alt="" />}
              <div className="special-event-info">
                <div className={`special-event-type type-${e.type}`}>{e.label}</div>
                <div className="special-event-detail">{e.detail}</div>
                <div className="special-event-match">
                  {e.match}
                  {e.kickoff && e.kickoff !== 'Tournament' && (
                    <span className="special-event-kickoff">{` · ${e.kickoff}`}</span>
                  )}
                </div>
                {e.note && <div className="special-event-note">{e.note}</div>}
                {e.users.map((u) => (
                  <NameLink key={u} name={u} className="special-event-user" />
                ))}
              </div>
              <div className={`special-event-points${e.points === 0 && e.note ? ' zero' : ''}`}>
                {`+${e.points}`}
              </div>
            </div>
          ))
      )}
    </div>
  );
}

export default SpecialEventsWC26;
